import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { getSelectedCustomer } from '../../../../../../_augmentt/selectors/customers'
import ViewEmployeeDataModal from '../../../modals/ViewEmployeeDataModal'
import { EMPTY_VALUE, TONE } from '../../data/constants'
import { getControlResult } from '../../data/scoring'
import { formatRelativeDate } from '../../data/reportCopy'
import Chip from '../../components/Chip/Chip'
import EmployeeUserCell from '../../components/EmployeeUserCell/EmployeeUserCell'
import EmptyState from '../../components/EmptyState/EmptyState'
import ReportTable from '../../components/ReportTable/ReportTable'
import SubBlock from '../../components/SubBlock/SubBlock'

const NON_HUMAN_ACCOUNT_TYPES = new Set(['service_principal', 'service_account', 'shared_mailbox', 'generic_account'])
const EMPTY_CELL = '-'
const MFA_STATUS_MESSAGE_IDS = {
  registered: 'AI_RISK_REPORT.STATUS.REGISTERED',
  not_registered: 'AI_RISK_REPORT.STATUS.NOT_REGISTERED',
  not_assessed: 'AI_RISK_REPORT.STATUS.NOT_ASSESSED',
  not_applicable: 'GENERAL.NONE',
  unknown: 'AI_RISK_REPORT.LABEL.UNKNOWN',
}

const prettifyValue = (value) =>
  String(value ?? '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())

const formatMfaStatus = (intl, status) => {
  const normalizedStatus = String(status ?? '').toLowerCase()
  if (!normalizedStatus) return EMPTY_CELL

  const messageId = MFA_STATUS_MESSAGE_IDS[normalizedStatus]
  if (!messageId) return prettifyValue(status)

  return intl.formatMessage({ id: messageId })
}

const getControlTone = (result) => {
  if (result === true) return TONE.OK
  if (result === false) return TONE.DANGER
  return TONE.NEUTRAL
}

export default function GlobalAdmins({ admins, score, controls, gap = null, summary }) {
  const intl = useIntl()
  const selectedCustomer = useSelector(getSelectedCustomer)
  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const openEmployee = useCallback((employee) => setSelectedEmployee(employee), [])
  const closeEmployee = useCallback(() => setSelectedEmployee(null), [])
  const isUnavailable = Boolean(gap)
  const totalAdmins = summary.users?.globalAdmins ?? 0
  const totalUsers = summary.users?.total ?? 0
  const adminPct = totalUsers > 0 ? Math.round((totalAdmins / totalUsers) * 100) : 0
  const nonHumanAdmins = admins.filter((admin) => NON_HUMAN_ACCOUNT_TYPES.has(admin.accountType)).length
  const namedHumanAdmins = Math.max(0, totalAdmins - nonHumanAdmins)
  const adminCountTone = getControlTone(getControlResult(controls, 'admins.count'))
  const nonHumanAdminTone = getControlTone(getControlResult(controls, 'admins.nonHuman'))

  const stats = [
    {
      key: 'total',
      value: isUnavailable ? EMPTY_VALUE : totalAdmins,
      tone: isUnavailable ? TONE.NEUTRAL : adminCountTone,
      label: isUnavailable ? (
        <FormattedMessage id="AI_RISK_REPORT.IDENTITY.ADMINS.STAT_TOTAL_UNAVAILABLE" />
      ) : (
        <FormattedMessage id="AI_RISK_REPORT.IDENTITY.ADMINS.STAT_TOTAL" values={{ pct: adminPct }} />
      ),
    },
    {
      key: 'nonHuman',
      value: isUnavailable ? EMPTY_VALUE : nonHumanAdmins,
      tone: nonHumanAdminTone,
      label: <FormattedMessage id="AI_RISK_REPORT.IDENTITY.ADMINS.STAT_NON_HUMAN" />,
    },
    {
      key: 'namedHuman',
      value: isUnavailable ? EMPTY_VALUE : namedHumanAdmins,
      label: <FormattedMessage id="AI_RISK_REPORT.IDENTITY.ADMINS.STAT_NAMED" />,
    },
  ]

  const columns = [
    {
      key: 'admin',
      header: <FormattedMessage id="AI_RISK_REPORT.IDENTITY.ADMINS.COL_ADMIN" />,
      render: (row) => (
        <EmployeeUserCell
          row={row}
          onOpen={openEmployee}
          secondary={row.mail || row.department || row.userPrincipalName}
        />
      ),
      sortable: true,
      sortValue: (row) => (row.displayName || row.userPrincipalName || '').toLowerCase(),
    },
    {
      key: 'lastSignIn',
      header: <FormattedMessage id="AI_RISK_REPORT.SKELETON.COL_LAST_SIGNIN" />,
      render: (row) => formatRelativeDate(intl, row.lastSignInAt),
      sortable: true,
      sortValue: (row) => row.lastSignInAt || '',
    },
    {
      key: 'mfa',
      header: <FormattedMessage id="AI_RISK_REPORT.SKELETON.COL_MFA" />,
      render: (row) => formatMfaStatus(intl, row.mfaStatus),
      sortable: true,
      sortValue: (row) => formatMfaStatus(intl, row.mfaStatus),
    },
    {
      key: 'issue',
      header: <FormattedMessage id="AI_RISK_REPORT.COL_ISSUE" />,
      render: (row) =>
        NON_HUMAN_ACCOUNT_TYPES.has(row.accountType) ? (
          <Chip tone={TONE.DANGER}>
            <FormattedMessage id="AI_RISK_REPORT.IDENTITY.ADMINS.ISSUE_NON_HUMAN" />
          </Chip>
        ) : (
          EMPTY_CELL
        ),
      sortable: true,
      sortValue: (row) => (NON_HUMAN_ACCOUNT_TYPES.has(row.accountType) ? 1 : 0),
    },
  ]

  return (
    <SubBlock
      titleId="AI_RISK_REPORT.IDENTITY.ADMINS.TITLE"
      subtitleId="AI_RISK_REPORT.IDENTITY.ADMINS.SUBTITLE"
      score={score.value}
      tone={score.tone}
      gap={gap}
      stats={stats}
    >
      <ReportTable
        columns={columns}
        rows={admins.map((admin) => ({ ...admin, key: admin.entityId }))}
        empty={<EmptyState messageId="AI_RISK_REPORT.NO_GLOBAL_ADMINS" />}
      />
      {selectedEmployee && (
        <ViewEmployeeDataModal
          show
          onHide={closeEmployee}
          tableRowEmployee={selectedEmployee}
          selectedCustomer={selectedCustomer}
        />
      )}
    </SubBlock>
  )
}

const summaryPropType = PropTypes.shape({
  users: PropTypes.shape({
    total: PropTypes.number,
    globalAdmins: PropTypes.number,
  }),
})

GlobalAdmins.propTypes = {
  admins: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  score: PropTypes.shape({ value: PropTypes.number, tone: PropTypes.string }).isRequired,
  controls: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, result: PropTypes.bool })).isRequired,
  gap: PropTypes.shape({ reason: PropTypes.string }),
  summary: summaryPropType.isRequired,
}
